import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ClipboardList, PackageCheck, FlaskConical, Gauge, Building, Ruler, Settings } from 'lucide-react';
import api from '@/lib/api';

// Universal search modal (⌘K / Ctrl+K).
// Backend endpoint: GET /api/search?q=&limit=
// Keyboard: ↑↓ pohyb, Enter otevřít, Esc zavřít.

const TYPE_META = {
  order: { label: 'Objednávky', icon: ClipboardList, color: 'var(--ct-warning)' },
  intake: { label: 'Příjmy', icon: PackageCheck, color: 'var(--ct-warning)' },
  calibration: { label: 'Kalibrace', icon: FlaskConical, color: 'var(--ct-info)' },
  instrument: { label: 'Přístroje', icon: Gauge, color: 'var(--ct-accent)' },
  company: { label: 'Společnosti', icon: Building, color: 'var(--ct-text-secondary)' },
  etalon: { label: 'Etalony', icon: Ruler, color: 'var(--ct-success)' },
  methodology: { label: 'Metodiky', icon: Settings, color: 'var(--ct-text-secondary)' },
};

function Kbd({ children }) {
  return (
    <kbd
      className="px-1 rounded border font-mono text-[10px]"
      style={{ background: 'var(--ct-surface)', borderColor: 'var(--ct-border)', color: 'var(--ct-text-secondary)' }}
    >
      {children}
    </kbd>
  );
}

export default function SearchModal({ open, onClose }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);

  // Při otevření reset state a focus input.
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setSelectedIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Debounced fetch při změně query.
  useEffect(() => {
    if (!open || !query.trim()) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get(`/search?q=${encodeURIComponent(query)}`);
        setResults(res.data.results || []);
        setSelectedIdx(0);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [query, open]);

  // Klávesnice: šipky, Enter, Esc.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') { onClose(); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, results.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, 0)); }
      else if (e.key === 'Enter' && results[selectedIdx]) { e.preventDefault(); handleSelect(results[selectedIdx]); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, results, selectedIdx]);

  const handleSelect = (item) => {
    if (item.href) navigate(item.href);
    onClose();
  };

  if (!open) return null;

  // Seskupit podle typu pro display (zachovává insertion order z backendu).
  const grouped = results.reduce((acc, r) => {
    (acc[r.type] ||= []).push(r);
    return acc;
  }, {});

  // Running index napříč skupinami pro keyboard nav match.
  let flatIdx = -1;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-lg overflow-hidden flex flex-col"
        style={{
          background: 'var(--ct-surface)',
          border: '1px solid var(--ct-border)',
          boxShadow: 'var(--ct-shadow-xl)',
          maxHeight: '70vh',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Input */}
        <div className="flex items-center gap-2 px-4 border-b" style={{ borderColor: 'var(--ct-border)' }}>
          <Search className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--ct-text-disabled)' }} />
          <input
            ref={inputRef}
            data-testid="search-input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Hledat objednávky, kalibrace, přístroje…"
            className="flex-1 py-3 text-sm bg-transparent outline-none border-0"
            style={{ color: 'var(--ct-text)' }}
          />
          <Kbd>Esc</Kbd>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="p-8 text-center text-xs" style={{ color: 'var(--ct-text-secondary)' }}>
              Hledám…
            </div>
          )}

          {!loading && query.trim() && results.length === 0 && (
            <div className="p-8 text-center text-sm" style={{ color: 'var(--ct-text-secondary)' }}>
              Žádné výsledky pro „{query}"
            </div>
          )}

          {!loading && !query.trim() && (
            <div className="p-8 text-center text-sm" style={{ color: 'var(--ct-text-secondary)' }}>
              Začněte psát — prohledá se napříč objednávkami, kalibracemi, přístroji, etalony a metodikami.
            </div>
          )}

          {!loading && Object.entries(grouped).map(([type, items]) => {
            const meta = TYPE_META[type] || { label: type, icon: Search, color: 'var(--ct-text-secondary)' };
            const Icon = meta.icon;
            return (
              <div key={type}>
                <div
                  className="ct-eyebrow px-4 py-1.5 sticky top-0 z-10"
                  style={{ background: 'var(--ct-bg-secondary)', borderBottom: '1px solid var(--ct-border)' }}
                >
                  {meta.label}
                </div>
                {items.map((r) => {
                  flatIdx += 1;
                  const isSelected = flatIdx === selectedIdx;
                  const myIdx = flatIdx;
                  return (
                    <button
                      key={`${r.type}-${r.id}`}
                      type="button"
                      onClick={() => handleSelect(r)}
                      onMouseEnter={() => setSelectedIdx(myIdx)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors"
                      style={{ background: isSelected ? 'var(--ct-surface-hover)' : 'transparent' }}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" style={{ color: meta.color }} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate" style={{ color: 'var(--ct-text)' }}>{r.label}</div>
                        {r.sublabel && (
                          <div className="text-xs truncate" style={{ color: 'var(--ct-text-secondary)' }}>{r.sublabel}</div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Footer: keyboard hints */}
        <div
          className="px-4 py-2 border-t flex items-center gap-3 text-[10px]"
          style={{ borderColor: 'var(--ct-border)', background: 'var(--ct-bg-secondary)', color: 'var(--ct-text-disabled)' }}
        >
          <span className="flex items-center gap-1"><Kbd>↑↓</Kbd>pohyb</span>
          <span className="flex items-center gap-1"><Kbd>↵</Kbd>otevřít</span>
          <span className="flex items-center gap-1"><Kbd>Esc</Kbd>zavřít</span>
          <div className="flex-1" />
          {results.length > 0 && <span>{results.length} výsledků</span>}
        </div>
      </div>
    </div>
  );
}
